import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SlideOver, SlideOverContent } from '@/components/ui/slide-over';
import { LoaderIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { RAGClassSchema, type RAGClass } from '@/proto/agentplatform/v1/rag_pb';
import { RAGSchema, type RAG } from '@/proto/agentplatform/v1/rag_pb';
import { ragPluginApi } from '@/api/ragPlugin.api';
import { ragApi, type RagCollectionRow } from '@/api/rag.api';
import { formatTimestamp } from '@/utils/utils';

type Tab = 'class' | 'instance';

const DEFAULT_CLASS_PARAMS_JSON = '[]';
const DEFAULT_RAG_SEARCH_TOP_K = 4;

function parseRagDefaultTopK(
  raw: string
): { ok: true; value: number } | { ok: false; message: string } {
  const n = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || n < 1) {
    return { ok: false, message: '默认 top_k 须为 1～100 的正整数' };
  }
  if (n > 100) {
    return { ok: false, message: '默认 top_k 不能超过 100' };
  }
  return { ok: true, value: n };
}

export default function RAGCatalogPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.get('tab') === 'instance' ? 'instance' : 'class'
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');

  const [showClassForm, setShowClassForm] = useState(false);
  const [className, setClassName] = useState('');
  const [classDescription, setClassDescription] = useState('');
  const [classTemplateProvider, setClassTemplateProvider] = useState('default');
  const [classCollectionIds, setClassCollectionIds] = useState<string[]>([]);
  const [classCollLeftChecked, setClassCollLeftChecked] = useState<string[]>([]);
  const [classCollRightChecked, setClassCollRightChecked] = useState<string[]>([]);
  const [classCollKeyword, setClassCollKeyword] = useState('');
  const [classParamsJson, setClassParamsJson] = useState(DEFAULT_CLASS_PARAMS_JSON);
  const [classDefaultSearchTopK, setClassDefaultSearchTopK] = useState(
    String(DEFAULT_RAG_SEARCH_TOP_K)
  );
  const [classFormError, setClassFormError] = useState('');

  const [showInstanceForm, setShowInstanceForm] = useState(false);
  const [instanceName, setInstanceName] = useState('');
  const [instanceDescription, setInstanceDescription] = useState('');
  const [instanceClassId, setInstanceClassId] = useState('');
  const [instanceCollectionIds, setInstanceCollectionIds] = useState<string[]>([]);
  const [instanceDefaultSearchTopK, setInstanceDefaultSearchTopK] = useState(
    String(DEFAULT_RAG_SEARCH_TOP_K)
  );
  const [instanceFormError, setInstanceFormError] = useState('');

  const [deleteClassId, setDeleteClassId] = useState<string | null>(null);
  const [deleteInstanceId, setDeleteInstanceId] = useState<string | null>(null);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(() => new Set());
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<Set<string>>(() => new Set());
  const [batchDeleteClassDialog, setBatchDeleteClassDialog] = useState<string[] | null>(null);
  const [batchDeleteInstanceDialog, setBatchDeleteInstanceDialog] = useState<string[] | null>(null);
  const [batchDeleteError, setBatchDeleteError] = useState<string | null>(null);
  const classTableHeaderSelectRef = useRef<HTMLInputElement | null>(null);
  const instanceTableHeaderSelectRef = useRef<HTMLInputElement | null>(null);

  const classQuery = useQuery({
    queryKey: ['rag-classes-catalog', page, pageSize, keyword],
    queryFn: () =>
      ragPluginApi.listRAGClasses(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: keyword.trim() ? { name: keyword.trim() } : {},
        })
      ),
  });

  const instanceQuery = useQuery({
    queryKey: ['rags-catalog', page, pageSize, keyword],
    queryFn: () =>
      ragPluginApi.listRAGs(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: keyword.trim() ? { name: keyword.trim() } : {},
        })
      ),
  });

  const collectionsForInstanceForm = useQuery({
    queryKey: ['rag-collections', 'rag-catalog-instance-form'],
    enabled: showInstanceForm,
    queryFn: () => ragApi.listRagCollections({ page: 1, pageSize: 200 }),
  });

  const collectionsForClassForm = useQuery({
    queryKey: ['rag-collections', 'rag-catalog-class-form'],
    enabled: showClassForm,
    queryFn: () => ragApi.listRagCollections({ page: 1, pageSize: 200 }),
  });

  const classes = useMemo(() => classQuery.data?.data ?? [], [classQuery.data?.data]);
  const instances = useMemo(() => instanceQuery.data?.data ?? [], [instanceQuery.data?.data]);

  useEffect(() => {
    const valid = new Set(classes.map((c) => c.id));
    setSelectedClassIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [classes]);

  useEffect(() => {
    const valid = new Set(instances.map((c) => c.id));
    setSelectedInstanceIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [instances]);

  const allClassesSelected = classes.length > 0 && classes.every((c) => selectedClassIds.has(c.id));
  const someClassesSelected = classes.some((c) => selectedClassIds.has(c.id));
  const allInstancesSelected =
    instances.length > 0 && instances.every((c) => selectedInstanceIds.has(c.id));
  const someInstancesSelected = instances.some((c) => selectedInstanceIds.has(c.id));

  useLayoutEffect(() => {
    const el = classTableHeaderSelectRef.current;
    if (!el) return;
    el.indeterminate = someClassesSelected && !allClassesSelected;
  }, [someClassesSelected, allClassesSelected]);

  useLayoutEffect(() => {
    const el = instanceTableHeaderSelectRef.current;
    if (!el) return;
    el.indeterminate = someInstancesSelected && !allInstancesSelected;
  }, [someInstancesSelected, allInstancesSelected]);

  const toggleClassRowSelected = useCallback((id: string) => {
    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllClasses = useCallback(() => {
    setSelectedClassIds((prev) => {
      if (classes.length === 0) return prev;
      if (classes.every((c) => prev.has(c.id))) return new Set();
      return new Set(classes.map((c) => c.id));
    });
  }, [classes]);

  const toggleInstanceRowSelected = useCallback((id: string) => {
    setSelectedInstanceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllInstances = useCallback(() => {
    setSelectedInstanceIds((prev) => {
      if (instances.length === 0) return prev;
      if (instances.every((c) => prev.has(c.id))) return new Set();
      return new Set(instances.map((c) => c.id));
    });
  }, [instances]);

  const classNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of classes) m.set(c.id, c.name);
    return m;
  }, [classes]);

  const instanceNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of instances) m.set(c.id, c.name);
    return m;
  }, [instances]);

  const createClassMutation = useMutation({
    mutationFn: (payload: RAGClass) => ragPluginApi.createRAGClass(payload),
    onSuccess: () => {
      setShowClassForm(false);
      setClassName('');
      setClassDescription('');
      setClassTemplateProvider('default');
      setClassCollectionIds([]);
      setClassCollLeftChecked([]);
      setClassCollRightChecked([]);
      setClassCollKeyword('');
      setClassParamsJson(DEFAULT_CLASS_PARAMS_JSON);
      setClassDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
      setClassFormError('');
      void queryClient.invalidateQueries({ queryKey: ['rag-classes-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-classes', 'agent-form'] });
    },
    onError: (e: unknown) => {
      setClassFormError(e instanceof Error ? e.message : String(e));
    },
  });

  const createInstanceMutation = useMutation({
    mutationFn: (payload: RAG) => ragPluginApi.createRAG(payload),
    onSuccess: () => {
      setShowInstanceForm(false);
      setInstanceName('');
      setInstanceDescription('');
      setInstanceClassId('');
      setInstanceCollectionIds([]);
      setInstanceDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
      setInstanceFormError('');
      void queryClient.invalidateQueries({ queryKey: ['rags-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rags-plugin', 'agent-form'] });
    },
    onError: (e: unknown) => {
      setInstanceFormError(e instanceof Error ? e.message : String(e));
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: (id: string) => ragPluginApi.deleteRAGClass(id),
    onSuccess: () => {
      setDeleteClassId(null);
      void queryClient.invalidateQueries({ queryKey: ['rag-classes-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-classes', 'agent-form'] });
    },
  });

  const deleteInstanceMutation = useMutation({
    mutationFn: (id: string) => ragPluginApi.deleteRAG(id),
    onSuccess: () => {
      setDeleteInstanceId(null);
      void queryClient.invalidateQueries({ queryKey: ['rags-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rags-plugin', 'agent-form'] });
    },
  });

  const deleteClassesBatchMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const settled = await Promise.allSettled(ids.map((id) => ragPluginApi.deleteRAGClass(id)));
      const deletedIds: string[] = [];
      const failed: { id: string; err: string }[] = [];
      settled.forEach((s, i) => {
        const id = ids[i];
        if (s.status === 'fulfilled') deletedIds.push(id);
        else
          failed.push({ id, err: s.reason instanceof Error ? s.reason.message : String(s.reason) });
      });
      return { deletedIds, failed };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['rag-classes-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-classes', 'agent-form'] });
      setSelectedClassIds((prev) => {
        const next = new Set(prev);
        for (const id of data.deletedIds) next.delete(id);
        return next;
      });
      setBatchDeleteClassDialog(null);
      if (data.failed.length > 0) {
        setBatchDeleteError(
          data.deletedIds.length > 0
            ? `已删除 ${data.deletedIds.length} 个模版；${data.failed.length} 个失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
            : `删除失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
        );
      } else {
        setBatchDeleteError(null);
      }
    },
  });

  const deleteInstancesBatchMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const settled = await Promise.allSettled(ids.map((id) => ragPluginApi.deleteRAG(id)));
      const deletedIds: string[] = [];
      const failed: { id: string; err: string }[] = [];
      settled.forEach((s, i) => {
        const id = ids[i];
        if (s.status === 'fulfilled') deletedIds.push(id);
        else
          failed.push({ id, err: s.reason instanceof Error ? s.reason.message : String(s.reason) });
      });
      return { deletedIds, failed };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['rags-catalog'] });
      void queryClient.invalidateQueries({ queryKey: ['rags-plugin', 'agent-form'] });
      setSelectedInstanceIds((prev) => {
        const next = new Set(prev);
        for (const id of data.deletedIds) next.delete(id);
        return next;
      });
      setBatchDeleteInstanceDialog(null);
      if (data.failed.length > 0) {
        setBatchDeleteError(
          data.deletedIds.length > 0
            ? `已删除 ${data.deletedIds.length} 个实例；${data.failed.length} 个失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
            : `删除失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
        );
      } else {
        setBatchDeleteError(null);
      }
    },
  });

  const resetTabFilter = (next: Tab) => {
    setTab(next);
    setPage(1);
    setKeyword('');
    setSelectedClassIds(new Set());
    setSelectedInstanceIds(new Set());
    setBatchDeleteError(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next === 'instance' ? 'instance' : 'class');
    setSearchParams(nextParams, { replace: true });
  };

  const handledCreateIntentRef = useRef('');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'instance') setTab('instance');
    else if (t === 'class') setTab('class');
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('create') !== '1') {
      handledCreateIntentRef.current = '';
      return;
    }
    if (searchParams.get('tab') !== 'instance') return;
    const classId = searchParams.get('classId') || '';
    const intentKey = `${classId}|create`;
    if (handledCreateIntentRef.current === intentKey) return;
    handledCreateIntentRef.current = intentKey;

    setTab('instance');
    setInstanceClassId(classId);
    setInstanceFormError('');
    setInstanceCollectionIds([]);
    setInstanceDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
    setShowInstanceForm(true);

    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('classId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const classOptions = useMemo(
    () =>
      classes.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name} ({c.id})
        </option>
      )),
    [classes]
  );

  const registeredClassCollections = useMemo(
    () => collectionsForClassForm.data?.collections ?? [],
    [collectionsForClassForm.data?.collections]
  );
  const classSelectedCollectionIds = classCollectionIds;
  const classCollectionRowById = useMemo(() => {
    const m = new Map<string, RagCollectionRow>();
    for (const c of registeredClassCollections) {
      m.set(c.id, c);
    }
    return m;
  }, [registeredClassCollections]);

  const classCollKeywordNorm = classCollKeyword.trim().toLowerCase();
  const classAvailableCollections = useMemo(() => {
    const sel = new Set(classSelectedCollectionIds);
    return registeredClassCollections.filter((c) => {
      if (sel.has(c.id)) return false;
      if (!classCollKeywordNorm) return true;
      const hay = `${c.collection_display_name}\n${c.id}\n${c.description ?? ''}`.toLowerCase();
      return hay.includes(classCollKeywordNorm);
    });
  }, [registeredClassCollections, classSelectedCollectionIds, classCollKeywordNorm]);

  const classSelectedCollectionRows = useMemo(
    () =>
      classSelectedCollectionIds.map((id) => {
        const row = classCollectionRowById.get(id);
        if (row) return row;
        return {
          id,
          collection_display_name: id,
          collection_name: id,
          qdrant_collection_name: 'AGENT_RAG_' + id,
          created_at: '',
        } satisfies RagCollectionRow;
      }),
    [classSelectedCollectionIds, classCollectionRowById]
  );

  const resetClassFormSideState = () => {
    setClassTemplateProvider('default');
    setClassCollectionIds([]);
    setClassCollLeftChecked([]);
    setClassCollRightChecked([]);
    setClassCollKeyword('');
  };

  const toggleClassCollLeftChecked = (id: string) => {
    setClassCollLeftChecked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleClassCollRightChecked = (id: string) => {
    setClassCollRightChecked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const addClassSelectedCollections = () => {
    if (classCollLeftChecked.length === 0) return;
    const existing = new Set(classCollectionIds);
    const toAdd = classCollLeftChecked.filter((id) => !existing.has(id));
    setClassCollectionIds([...classCollectionIds, ...toAdd]);
    setClassCollLeftChecked([]);
  };
  const addAllClassVisibleCollections = () => {
    if (classAvailableCollections.length === 0) return;
    const existing = new Set(classCollectionIds);
    const toAdd = classAvailableCollections.filter((c) => !existing.has(c.id)).map((c) => c.id);
    setClassCollectionIds([...classCollectionIds, ...toAdd]);
    setClassCollLeftChecked([]);
  };
  const removeClassSelectedCollections = () => {
    if (classCollRightChecked.length === 0) return;
    const removed = new Set(classCollRightChecked);
    setClassCollectionIds(classCollectionIds.filter((id) => !removed.has(id)));
    setClassCollRightChecked([]);
  };
  const removeAllClassCollections = () => {
    setClassCollectionIds([]);
    setClassCollRightChecked([]);
  };
  const submitClass = () => {
    setClassFormError('');
    const name = className.trim();
    if (!name) {
      setClassFormError('请输入名称');
      return;
    }
    if (classCollectionIds.length === 0) {
      setClassFormError('请至少选择一个已在「知识库管理」登记的知识库集合');
      return;
    }
    let paramsJson: unknown;
    try {
      paramsJson = JSON.parse(classParamsJson.trim() || '[]');
    } catch {
      setClassFormError('参数 JSON 无效');
      return;
    }
    if (!Array.isArray(paramsJson)) {
      setClassFormError('参数必须是 JSON 数组');
      return;
    }
    const topKParsed = parseRagDefaultTopK(classDefaultSearchTopK);
    if (!topKParsed.ok) {
      setClassFormError(topKParsed.message);
      return;
    }
    const prov = classTemplateProvider.trim() || 'default';
    const template = create(RAGSchema, {
      name: name,
      description: classDescription.trim(),
      provider: prov,
      collections: classCollectionIds,
      defaultSearchTopK: topKParsed.value,
    });
    const payload = create(RAGClassSchema, {
      name,
      description: classDescription.trim(),
      template,
      params: paramsJson as RAGClass['params'],
    });
    createClassMutation.mutate(payload);
  };

  const submitInstance = () => {
    setInstanceFormError('');
    const name = instanceName.trim();
    if (!name) {
      setInstanceFormError('请输入名称');
      return;
    }
    if (!instanceClassId.trim()) {
      setInstanceFormError('请选择模版');
      return;
    }
    if (instanceCollectionIds.length === 0) {
      setInstanceFormError('请至少选择一个已在「知识库管理」登记的知识库集合');
      return;
    }
    const classParams: Record<string, string> = {};
    const topKParsed = parseRagDefaultTopK(instanceDefaultSearchTopK);
    if (!topKParsed.ok) {
      setInstanceFormError(topKParsed.message);
      return;
    }
    const payload = create(RAGSchema, {
      name,
      description: instanceDescription.trim(),
      classId: instanceClassId.trim(),
      classParams,
      provider: 'default',
      collections: instanceCollectionIds,
      defaultSearchTopK: topKParsed.value,
    });
    createInstanceMutation.mutate(payload);
  };

  const registeredCollections = collectionsForInstanceForm.data?.collections ?? [];

  const toggleInstanceCollection = (cid: string, checked: boolean) => {
    setInstanceCollectionIds((prev) =>
      checked ? (prev.includes(cid) ? prev : [...prev, cid]) : prev.filter((id) => id !== cid)
    );
  };

  const activeData = tab === 'class' ? classQuery.data : instanceQuery.data;
  const activeLoading = tab === 'class' ? classQuery.isLoading : instanceQuery.isLoading;
  const activeError = tab === 'class' ? classQuery.isError : instanceQuery.isError;
  const total = activeData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeSelectedCount = tab === 'class' ? selectedClassIds.size : selectedInstanceIds.size;
  const batchDeletePending =
    deleteClassesBatchMutation.isPending || deleteInstancesBatchMutation.isPending;

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">知识库组</h1>
          <p className="text-sm text-muted-foreground mt-1">
            模版定义检索参数与默认集合；实例基于模版创建，勾选已在「知识库管理」登记的集合。PDF
            入库仍在「知识库管理」中操作。
          </p>
        </div>

        <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <button
                type="button"
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  tab === 'class'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => resetTabFilter('class')}
              >
                模版
              </button>
              <button
                type="button"
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  tab === 'instance'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => resetTabFilter('instance')}
              >
                实例
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="w-56 h-9"
                placeholder="按名称搜索"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  tab === 'class' ? void classQuery.refetch() : void instanceQuery.refetch()
                }
              >
                刷新
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={activeSelectedCount === 0 || activeLoading || batchDeletePending}
                onClick={() => {
                  setBatchDeleteError(null);
                  if (tab === 'class') {
                    const ids = Array.from(selectedClassIds);
                    if (ids.length === 0) return;
                    deleteClassesBatchMutation.reset();
                    setBatchDeleteClassDialog(ids);
                  } else {
                    const ids = Array.from(selectedInstanceIds);
                    if (ids.length === 0) return;
                    deleteInstancesBatchMutation.reset();
                    setBatchDeleteInstanceDialog(ids);
                  }
                }}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                批量删除
                {activeSelectedCount > 0 ? ` (${activeSelectedCount})` : ''}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (tab === 'class') {
                    setClassFormError('');
                    setClassDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
                    resetClassFormSideState();
                    setShowClassForm(true);
                  } else {
                    setInstanceFormError('');
                    setInstanceCollectionIds([]);
                    setInstanceDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
                    setShowInstanceForm(true);
                  }
                }}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                新建
              </Button>
            </div>
          </div>

          {batchDeleteError ? (
            <div className="mx-5 mt-3 text-sm text-destructive whitespace-pre-wrap rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
              {batchDeleteError}
            </div>
          ) : null}

          {activeLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : activeError ? (
            <div className="p-8 text-sm text-destructive">加载失败，请重试。</div>
          ) : tab === 'class' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pr-0">
                    <input
                      ref={classTableHeaderSelectRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-input align-middle"
                      checked={allClassesSelected}
                      disabled={classes.length === 0 || batchDeletePending}
                      onChange={toggleSelectAllClasses}
                      aria-label="全选模版"
                    />
                  </TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead className="w-28">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      暂无模版定义，点击右上角「新建」创建。
                    </TableCell>
                  </TableRow>
                ) : (
                  classes.map((row) => (
                    <TableRow
                      key={row.id}
                      className="hover:bg-muted/20 cursor-pointer"
                      onClick={() => navigate(`/plugins/rags/group-classes/${row.id}`)}
                    >
                      <TableCell className="w-10 pr-0" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-input"
                          checked={selectedClassIds.has(row.id)}
                          disabled={batchDeletePending}
                          onChange={() => toggleClassRowSelected(row.id)}
                          aria-label={`选择 ${row.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="font-medium text-primary hover:text-primary/80 text-left"
                          onClick={() => navigate(`/plugins/rags/group-classes/${row.id}`)}
                        >
                          {row.name}
                        </button>
                        <div className="text-xs text-muted-foreground">ID: {row.id}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-md truncate">
                        {row.description || '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {formatTimestamp(row.updatedAt)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteClassId(row.id)}
                            aria-label="删除"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pr-0">
                    <input
                      ref={instanceTableHeaderSelectRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-input align-middle"
                      checked={allInstancesSelected}
                      disabled={instances.length === 0 || batchDeletePending}
                      onChange={toggleSelectAllInstances}
                      aria-label="全选实例"
                    />
                  </TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>模版 ID</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>集合数</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead className="w-28">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      暂无实例。请先在「知识库管理」登记集合，再于此处新建并勾选集合。
                    </TableCell>
                  </TableRow>
                ) : (
                  instances.map((row) => (
                    <TableRow
                      key={row.id}
                      className="hover:bg-muted/20 cursor-pointer"
                      onClick={() => navigate(`/plugins/rags/groups/${row.id}`)}
                    >
                      <TableCell className="w-10 pr-0" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-input"
                          checked={selectedInstanceIds.has(row.id)}
                          disabled={batchDeletePending}
                          onChange={() => toggleInstanceRowSelected(row.id)}
                          aria-label={`选择 ${row.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="font-medium text-primary hover:text-primary/80 text-left"
                          onClick={() => navigate(`/plugins/rags/groups/${row.id}`)}
                        >
                          {row.name}
                        </button>
                        <div className="text-xs text-muted-foreground">ID: {row.id}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-md truncate">
                        {row.description || '—'}
                      </TableCell>
                      <TableCell className="font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                        {row.classId ? (
                          <button
                            type="button"
                            className="text-primary hover:text-primary/80"
                            onClick={() => navigate(`/plugins/rags/group-classes/${row.classId}`)}
                          >
                            {row.classId}
                          </button>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {row.provider || '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {(row.collections || []).length}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {formatTimestamp(row.updatedAt)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteInstanceId(row.id)}
                            aria-label="删除"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <div className="px-5 py-3 border-t flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">共 {total} 条</span>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <span>
                {page}/{totalPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </Button>
            </div>
          </div>
        </div>

        <SlideOver
          open={showClassForm}
          onOpenChange={(open) => {
            if (!open) {
              setShowClassForm(false);
              setClassName('');
              setClassDescription('');
              setClassParamsJson(DEFAULT_CLASS_PARAMS_JSON);
              setClassDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
              setClassFormError('');
              resetClassFormSideState();
            } else {
              setShowClassForm(true);
              void collectionsForClassForm.refetch();
            }
          }}
          title="新建模版"
          description="与技能组模版一致：填写名称与说明，从已登记集合中挑选默认模板；参数仍为 Param 数组 JSON。"
          actions={
            <Button
              type="button"
              onClick={submitClass}
              disabled={
                !className.trim() ||
                classSelectedCollectionIds.length === 0 ||
                createClassMutation.isPending
              }
            >
              {createClassMutation.isPending ? (
                <span className="inline-flex items-center">
                  <LoaderIcon className="h-4 w-4 animate-spin mr-2" />
                  创建中
                </span>
              ) : (
                '创建'
              )}
            </Button>
          }
        >
          <SlideOverContent>
            <div className="space-y-4 p-1">
              <div className="space-y-1.5">
                <p className="text-sm font-medium">名称 *</p>
                <Input value={className} onChange={(e) => setClassName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-medium">描述</p>
                <textarea
                  className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={classDescription}
                  onChange={(e) => setClassDescription(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="class-default-top-k">默认检索条数（top_k）</Label>
                <Input
                  id="class-default-top-k"
                  type="number"
                  min={1}
                  max={100}
                  className="max-w-[120px]"
                  value={classDefaultSearchTopK}
                  onChange={(e) => setClassDefaultSearchTopK(e.target.value)}
                />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  智能体调用知识库工具且未单独指定 top_k
                  时，每次向量检索最多返回几条文本片段。数值越大召回越全，但占用上下文越多，常见取
                  4～10。
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">默认知识库集合 *</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void collectionsForClassForm.refetch()}
                  >
                    刷新集合列表
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  从已登记集合中挑选作为模板默认集合；集合须在「知识库管理」中先登记。
                </p>
                <Input
                  placeholder="搜索可选集合（名称、ID、说明）"
                  value={classCollKeyword}
                  onChange={(e) => setClassCollKeyword(e.target.value)}
                />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
                  <div className="rounded-md border bg-background">
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                      可选集合（{classAvailableCollections.length}）
                    </div>
                    <div className="max-h-56 overflow-auto p-1">
                      {collectionsForClassForm.isLoading ? (
                        <p className="text-xs text-muted-foreground px-2 py-2">加载中…</p>
                      ) : collectionsForClassForm.isError ? (
                        <p className="text-xs text-destructive px-2 py-2">集合列表加载失败</p>
                      ) : classAvailableCollections.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-2 py-2">
                          {registeredClassCollections.length === 0
                            ? '暂无已登记集合，请先到「知识库管理」创建。'
                            : '没有匹配的未选集合'}
                        </p>
                      ) : (
                        classAvailableCollections.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => toggleClassCollLeftChecked(item.id)}
                            className={cn(
                              'w-full text-left px-2 py-1.5 rounded text-xs',
                              classCollLeftChecked.includes(item.id)
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted'
                            )}
                          >
                            <div className="font-medium break-all">
                              {item.collection_display_name}
                            </div>
                            <div className="text-muted-foreground mt-0.5 font-mono text-[11px] break-all">
                              {item.id}
                            </div>
                            {item.description?.trim() ? (
                              <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden text-[11px]">
                                {item.description}
                              </div>
                            ) : null}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 justify-center">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addClassSelectedCollections}
                    >
                      &gt;
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addAllClassVisibleCollections}
                    >
                      &gt;&gt;
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={removeClassSelectedCollections}
                    >
                      &lt;
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={removeAllClassCollections}
                    >
                      &lt;&lt;
                    </Button>
                  </div>
                  <div className="rounded-md border bg-background">
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                      已选集合（{classSelectedCollectionRows.length}）
                    </div>
                    <div className="max-h-56 overflow-auto p-1">
                      {classSelectedCollectionRows.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-2 py-2">未选择集合</p>
                      ) : (
                        classSelectedCollectionRows.map((item) => (
                          <div
                            key={item.id}
                            className={cn(
                              'px-2 py-1.5 rounded text-xs',
                              classCollRightChecked.includes(item.id)
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted'
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => toggleClassCollRightChecked(item.id)}
                              className="w-full text-left"
                            >
                              <div className="font-medium break-all">
                                {item.collection_display_name}
                              </div>
                              <div className="text-muted-foreground mt-0.5 font-mono text-[11px] break-all">
                                {item.id}
                              </div>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {classFormError ? <p className="text-sm text-destructive">{classFormError}</p> : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowClassForm(false);
                    setClassName('');
                    setClassDescription('');
                    setClassParamsJson(DEFAULT_CLASS_PARAMS_JSON);
                    setClassDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
                    setClassFormError('');
                    resetClassFormSideState();
                  }}
                >
                  取消
                </Button>
              </div>
            </div>
          </SlideOverContent>
        </SlideOver>

        <SlideOver
          open={showInstanceForm}
          onOpenChange={(open) => {
            setShowInstanceForm(open);
            if (!open) {
              setInstanceName('');
              setInstanceDescription('');
              setInstanceClassId('');
              setInstanceCollectionIds([]);
              setInstanceDefaultSearchTopK(String(DEFAULT_RAG_SEARCH_TOP_K));
              setInstanceFormError('');
            }
          }}
          title="新建实例"
          description="基于已定义的模版生成可挂载到智能体的实例。"
        >
          <SlideOverContent>
            <div className="space-y-4 p-1">
              <div className="space-y-1">
                <Label>名称</Label>
                <Input value={instanceName} onChange={(e) => setInstanceName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>说明</Label>
                <Input
                  value={instanceDescription}
                  onChange={(e) => setInstanceDescription(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>模版</Label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={instanceClassId}
                  onChange={(e) => setInstanceClassId(e.target.value)}
                >
                  <option value="">选择模版…</option>
                  {classOptions}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="instance-default-top-k">默认检索条数（top_k）</Label>
                <Input
                  id="instance-default-top-k"
                  type="number"
                  min={1}
                  max={100}
                  className="max-w-[120px]"
                  value={instanceDefaultSearchTopK}
                  onChange={(e) => setInstanceDefaultSearchTopK(e.target.value)}
                />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  智能体调用知识库工具且未单独指定 top_k
                  时，每次向量检索最多返回几条文本片段。数值越大召回越全，但占用上下文越多，常见取
                  4～10。
                </p>
              </div>
              <div className="space-y-2">
                <Label>知识库集合 *</Label>
                <p className="text-xs text-muted-foreground">
                  勾选「知识库管理」中已登记的 Qdrant collection。
                </p>
                {collectionsForInstanceForm.isLoading ? (
                  <p className="text-xs text-muted-foreground">加载集合列表…</p>
                ) : collectionsForInstanceForm.isError ? (
                  <p className="text-xs text-destructive">集合列表加载失败，请稍后重试。</p>
                ) : registeredCollections.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    当前账号下暂无已登记集合，请先到「知识库管理」创建。
                  </p>
                ) : (
                  <div className="max-h-56 overflow-auto rounded-md border divide-y">
                    {registeredCollections.map((c) => {
                      const checked = instanceCollectionIds.includes(c.id);
                      return (
                        <div
                          key={c.id}
                          className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            onChange={(e) => toggleInstanceCollection(c.id, e.target.checked)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{c.collection_display_name}</div>
                            <div className="text-xs text-muted-foreground font-mono truncate">
                              {c.id}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {instanceFormError ? (
                <p className="text-sm text-destructive">{instanceFormError}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowInstanceForm(false)}>
                  取消
                </Button>
                <Button
                  type="button"
                  onClick={submitInstance}
                  disabled={createInstanceMutation.isPending}
                >
                  {createInstanceMutation.isPending ? (
                    <>
                      <LoaderIcon className="h-4 w-4 animate-spin mr-2" />
                      创建中
                    </>
                  ) : (
                    '创建'
                  )}
                </Button>
              </div>
            </div>
          </SlideOverContent>
        </SlideOver>

        <AlertDialog
          open={deleteClassId !== null}
          onOpenChange={(o) => !o && setDeleteClassId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>删除此模版？</AlertDialogTitle>
              <AlertDialogDescription>
                删除后不可恢复；若仍有实例引用请谨慎操作。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deleteClassId && deleteClassMutation.mutate(deleteClassId)}
              >
                删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={deleteInstanceId !== null}
          onOpenChange={(o) => !o && setDeleteInstanceId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>删除该实例？</AlertDialogTitle>
              <AlertDialogDescription>
                将删除平台 raggroups 表中的实例记录；不会自动删除「知识库管理」中的 rag_collections
                / Qdrant collection。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deleteInstanceId && deleteInstanceMutation.mutate(deleteInstanceId)}
              >
                删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={batchDeleteClassDialog !== null && batchDeleteClassDialog.length > 0}
          onOpenChange={(open) => {
            if (!open) {
              setBatchDeleteClassDialog(null);
              deleteClassesBatchMutation.reset();
            }
          }}
        >
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>
                确认删除选中的 {batchDeleteClassDialog?.length ?? 0} 个模版？
              </AlertDialogTitle>
              <AlertDialogDescription>
                删除后不可恢复；若仍有实例引用该模版请谨慎操作。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="list-disc pl-4 space-y-0.5 max-h-36 overflow-y-auto text-sm text-foreground">
              {(batchDeleteClassDialog ?? []).slice(0, 12).map((id) => (
                <li key={id}>{classNameById.get(id) ?? id}</li>
              ))}
            </ul>
            {(batchDeleteClassDialog?.length ?? 0) > 12 ? (
              <p className="text-xs text-muted-foreground">
                … 等共 {batchDeleteClassDialog?.length} 项
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setBatchDeleteClassDialog(null);
                  deleteClassesBatchMutation.reset();
                }}
              >
                取消
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  deleteClassesBatchMutation.isPending ||
                  !batchDeleteClassDialog ||
                  batchDeleteClassDialog.length === 0
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!batchDeleteClassDialog?.length) return;
                  deleteClassesBatchMutation.mutate(batchDeleteClassDialog);
                }}
              >
                {deleteClassesBatchMutation.isPending ? (
                  <span className="inline-flex items-center">
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                    删除中…
                  </span>
                ) : (
                  '确认删除'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={batchDeleteInstanceDialog !== null && batchDeleteInstanceDialog.length > 0}
          onOpenChange={(open) => {
            if (!open) {
              setBatchDeleteInstanceDialog(null);
              deleteInstancesBatchMutation.reset();
            }
          }}
        >
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>
                确认删除选中的 {batchDeleteInstanceDialog?.length ?? 0} 个实例？
              </AlertDialogTitle>
              <AlertDialogDescription>
                将删除平台 raggroups 表中的实例记录；不会自动删除「知识库管理」中的 rag_collections
                / Qdrant collection。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="list-disc pl-4 space-y-0.5 max-h-36 overflow-y-auto text-sm text-foreground">
              {(batchDeleteInstanceDialog ?? []).slice(0, 12).map((id) => (
                <li key={id}>{instanceNameById.get(id) ?? id}</li>
              ))}
            </ul>
            {(batchDeleteInstanceDialog?.length ?? 0) > 12 ? (
              <p className="text-xs text-muted-foreground">
                … 等共 {batchDeleteInstanceDialog?.length} 项
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setBatchDeleteInstanceDialog(null);
                  deleteInstancesBatchMutation.reset();
                }}
              >
                取消
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  deleteInstancesBatchMutation.isPending ||
                  !batchDeleteInstanceDialog ||
                  batchDeleteInstanceDialog.length === 0
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!batchDeleteInstanceDialog?.length) return;
                  deleteInstancesBatchMutation.mutate(batchDeleteInstanceDialog);
                }}
              >
                {deleteInstancesBatchMutation.isPending ? (
                  <span className="inline-flex items-center">
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                    删除中…
                  </span>
                ) : (
                  '确认删除'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
